import React, { useState, useEffect } from 'react';

const DEFAULTS = {
  drop_invalid: true,
  default_action: 'drop',
  anti_spoof: false,
  rules: [],
  nat_rules: [],
};

const DEFAULT_FILTER_RULE = { chain: 'input', action: 'drop', protocol: '', src_address: '', dst_address: '', dst_port: '', in_interface: '', out_interface: '', connection_state: '', comment: '' };

const DEFAULT_NAT_RULE = { chain: 'dstnat', protocol: 'tcp', dst_port: '', in_interface: '', out_interface: '', src_address: '', dst_address: '', action: 'dst-nat', to_addresses: '', to_ports: '', comment: '' };

export default function StepFirewall({ data, onChange, onNext, onBack, interfaceNames }) {
  const [form, setForm] = useState(data || DEFAULTS);
  const [activeTab, setActiveTab] = useState('filter');
  const [newRule, setNewRule] = useState({ ...DEFAULT_FILTER_RULE });
  const [newNatRule, setNewNatRule] = useState({ ...DEFAULT_NAT_RULE });
  const [errorMsg, setErrorMsg] = useState('');
  const [natErrorMsg, setNatErrorMsg] = useState('');

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const update = (key, value) => {
    const updated = { ...form, [key]: value };
    setForm(updated);
    onChange(updated);
  };

  const addRule = () => {
    const comment = newRule.comment.trim();
    if (!comment) { setErrorMsg('กรุณากรอกคำอธิบายกฎ'); return; }
    setErrorMsg('');
    const updated = { ...form, rules: [...(form.rules || []), { ...newRule, comment }] };
    setForm(updated);
    onChange(updated);
    setNewRule({ ...DEFAULT_FILTER_RULE });
  };

  const removeRule = (index) => {
    const updated = { ...form, rules: form.rules.filter((_, i) => i !== index) };
    setForm(updated);
    onChange(updated);
  };

  const addNatRule = () => {
    const comment = newNatRule.comment.trim();
    if (!comment) { setNatErrorMsg('กรุณากรอกคำอธิบายกฎ'); return; }
    if (newNatRule.action === 'dst-nat' && !newNatRule.to_addresses) { setNatErrorMsg('กรุณากรอก IP ปลายทาง (To Addresses)'); return; }
    if (newNatRule.action === 'srcnat' && !newNatRule.to_addresses) { setNatErrorMsg('กรุณากรอก IP ปลายทาง (To Addresses)'); return; }
    setNatErrorMsg('');
    const updated = { ...form, nat_rules: [...(form.nat_rules || []), { ...newNatRule, comment }] };
    setForm(updated);
    onChange(updated);
    setNewNatRule({ ...DEFAULT_NAT_RULE });
  };

  const removeNatRule = (index) => {
    const updated = { ...form, nat_rules: form.nat_rules.filter((_, i) => i !== index) };
    setForm(updated);
    onChange(updated);
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); addRule(); } };
  const handleNatKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); addNatRule(); } };

  const chainLabels = { input: 'เข้า Router', forward: 'ผ่าน Router', output: 'ออกจาก Router' };
  const natChainLabels = { srcnat: 'ออกจาก Router (Source NAT)', dstnat: 'เข้า Router (Destination NAT)' };
  const natActionLabels = {
    'dst-nat': 'เปลี่ยนปลายทาง (Destination NAT)',
    'src-nat': 'เปลี่ยนต้นทาง (Source NAT)',
    'masquerade': 'Masquerade (ใช้ IP ของ WAN)',
    'netmap': 'Netmap (แมป IP 1:1)',
    'redirect': 'Redirect (เปลี่ยนพอร์ต)',
    'accept': 'อนุญาต (ไม่ทำ NAT)',
    'drop': 'บล็อก (ไม่ทำ NAT)',
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">🛡️ ตั้งค่าไฟร์วอลล์</h2>
        <p className="text-gray-500 dark:text-gray-400">ตั้งค่ากฎความปลอดภัย, NAT, ป้องกันการบุกรุก</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { key: 'filter', label: '🛡️ Filter Rules', desc: 'กฎกรองแพ็กเก็ต' },
          { key: 'nat', label: '🔄 NAT Rules', desc: 'Source NAT, Destination NAT, Masquerade' },
          { key: 'basic', label: '⚙️ ตั้งค่าพื้นฐาน', desc: 'Drop Invalid, Anti-Spoof' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 p-3 rounded-xl border-2 text-left transition-all ${
              activeTab === tab.key
                ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-600 dark:text-gray-400'
            }`}
          >
            <div className="font-bold text-sm">{tab.label}</div>
            <div className="text-xs opacity-70">{tab.desc}</div>
          </button>
        ))}
      </div>

      {/* Basic Settings Tab */}
      {activeTab === 'basic' && (
        <div className="space-y-4 mb-6">
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800/50 rounded-xl">
            <h3 className="font-bold text-green-800 dark:text-green-300 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
              กฎป้องกันพื้นฐาน (สร้างอัตโนมัติ)
            </h3>
            <div className="space-y-2 text-sm text-green-700 dark:text-green-300">
              <div className="flex items-center gap-2">✅ อนุญาตการเชื่อมต่อเดิม (Established/Related)</div>
              <div className="flex items-center gap-2">✅ อนุญาต Ping (ICMP)</div>
              <div className="flex items-center gap-2">✅ อนุญาตเครือข่าย LAN</div>
            </div>
          </div>

          <label className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 cursor-pointer hover:border-red-200 dark:hover:border-red-700 transition-colors">
            <div>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">บล็อกการเชื่อมต่อผิดปกติ (Drop Invalid)</span>
              <p className="text-xs text-gray-400 mt-0.5">ทิ้งแพ็กเก็ตที่สถานะการเชื่อมต่อไม่ถูกต้อง</p>
            </div>
            <input type="checkbox" checked={form.drop_invalid} onChange={e => update('drop_invalid', e.target.checked)} className="w-5 h-5 text-red-600 rounded" />
          </label>

          <label className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 cursor-pointer hover:border-red-200 dark:hover:border-red-700 transition-colors">
            <div>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">ป้องกัน IP ปลอม (Anti-Spoof)</span>
              <p className="text-xs text-gray-400 mt-0.5">บล็อกแพ็กเก็ตจาก IP ที่น่าสงสัย</p>
            </div>
            <input type="checkbox" checked={form.anti_spoof} onChange={e => update('anti_spoof', e.target.checked)} className="w-5 h-5 text-red-600 rounded" />
          </label>

          <div>
            <label className="label">นโยบายเริ่มต้น (Input Chain)</label>
            <select value={form.default_action} onChange={e => update('default_action', e.target.value)} className="input-field">
              <option value="drop">🔒 บล็อกทั้งหมด (แนะนำ)</option>
              <option value="accept">🔓 อนุญาตทั้งหมด</option>
            </select>
          </div>
        </div>
      )}

      {/* Filter Rules Tab */}
      {activeTab === 'filter' && (
        <div>
          {form.rules && form.rules.length > 0 && (
            <div className="space-y-2 mb-4">
              {form.rules.map((rule, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800/50 animate-fadeIn">
                  <div className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                    <span className="inline-flex items-center gap-1">
                      <span className="font-mono text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">{rule.chain}</span>
                      <span className="text-gray-400">→</span>
                      <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${rule.action === 'drop' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' : rule.action === 'reject' ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300' : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'}`}>{rule.action}</span>
                    </span>
                    {rule.protocol && <span className="ml-1 text-xs text-gray-400">({rule.protocol})</span>}
                    {rule.in_interface && <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">in:{rule.in_interface}</span>}
                    {rule.out_interface && <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">out:{rule.out_interface}</span>}
                    {rule.src_address && <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">จาก {rule.src_address}</span>}
                    {rule.dst_address && <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">ไป {rule.dst_address}</span>}
                    {rule.dst_port && <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">พอร์ต {rule.dst_port}</span>}
                    <div className="text-xs text-gray-400 mt-1">💬 {rule.comment}</div>
                  </div>
                  <button onClick={() => removeRule(i)} className="text-red-400 hover:text-red-600 ml-2 text-xl leading-none flex-shrink-0" title="ลบกฎนี้">×</button>
                </div>
              ))}
            </div>
          )}

          {/* Add Filter Rule Form */}
          <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl border border-gray-200 dark:border-gray-600">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-3">➕ เพิ่มกฎ Filter ใหม่</p>

            {errorMsg && (
              <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-xs text-red-600 dark:text-red-400 animate-fadeIn">
                ⚠️ {errorMsg}
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
              <div>
                <label className="label text-xs">Chain</label>
                <select value={newRule.chain} onChange={e => setNewRule({ ...newRule, chain: e.target.value })} className="input-field text-sm">
                  <option value="input">input ({chainLabels.input})</option>
                  <option value="forward">forward ({chainLabels.forward})</option>
                  <option value="output">output ({chainLabels.output})</option>
                </select>
              </div>
              <div>
                <label className="label text-xs">Action</label>
                <select value={newRule.action} onChange={e => setNewRule({ ...newRule, action: e.target.value })} className="input-field text-sm">
                  <option value="drop">🚫 บล็อก (drop)</option>
                  <option value="reject">❌ ปฏิเสธ (reject)</option>
                  <option value="accept">✅ อนุญาต (accept)</option>
                  <option value="log">📝 บันทึก Log เท่านั้น</option>
                  <option value="jump">⏭️ Jump</option>
                </select>
              </div>
              <div>
                <label className="label text-xs">โปรโตคอล</label>
                <input type="text" placeholder="เช่น tcp, udp, icmp" value={newRule.protocol} onChange={e => setNewRule({ ...newRule, protocol: e.target.value })} className="input-field text-sm" />
              </div>
              <div>
                <label className="label text-xs">In. Interface (ขาเข้า)</label>
                <select value={newRule.in_interface} onChange={e => setNewRule({ ...newRule, in_interface: e.target.value })} className="input-field text-sm">
                  <option value="">-- ทั้งหมด --</option>
                  {(interfaceNames && interfaceNames.length > 0 ? interfaceNames : ['ether1', 'ether2', 'ether3', 'ether4', 'ether5', 'bridge1', 'sfp-sfpplus1']).map(iface => (
                    <option key={iface} value={iface}>{iface}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label text-xs">Out. Interface (ขาออก)</label>
                <select value={newRule.out_interface} onChange={e => setNewRule({ ...newRule, out_interface: e.target.value })} className="input-field text-sm">
                  <option value="">-- ทั้งหมด --</option>
                  {(interfaceNames && interfaceNames.length > 0 ? interfaceNames : ['ether1', 'ether2', 'ether3', 'ether4', 'ether5', 'bridge1', 'sfp-sfpplus1']).map(iface => (
                    <option key={iface} value={iface}>{iface}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label text-xs">Connection State</label>
                <select value={newRule.connection_state} onChange={e => setNewRule({ ...newRule, connection_state: e.target.value })} className="input-field text-sm">
                  <option value="">-- ทั้งหมด --</option>
                  <option value="new">New (เชื่อมต่อใหม่)</option>
                  <option value="established">Established (เชื่อมต่อแล้ว)</option>
                  <option value="related">Related (เกี่ยวข้อง)</option>
                  <option value="invalid">Invalid (ผิดปกติ)</option>
                  <option value="untracked">Untracked</option>
                </select>
              </div>
              <div>
                <label className="label text-xs">IP ต้นทาง</label>
                <input type="text" placeholder="เช่น 192.168.20.0/24" value={newRule.src_address} onChange={e => setNewRule({ ...newRule, src_address: e.target.value })} className="input-field text-sm" />
              </div>
              <div>
                <label className="label text-xs">IP ปลายทาง</label>
                <input type="text" placeholder="เช่น 192.168.10.0/24" value={newRule.dst_address} onChange={e => setNewRule({ ...newRule, dst_address: e.target.value })} className="input-field text-sm" />
              </div>
              <div>
                <label className="label text-xs">พอร์ตปลายทาง</label>
                <input type="text" placeholder="เช่น 80, 443, 22" value={newRule.dst_port} onChange={e => setNewRule({ ...newRule, dst_port: e.target.value })} className="input-field text-sm" />
              </div>
            </div>
            <div className="flex gap-2">
              <input type="text" placeholder="💬 คำอธิบายกฎ (จำเป็นต้องกรอก)" value={newRule.comment} onChange={e => { setNewRule({ ...newRule, comment: e.target.value }); setErrorMsg(''); }} onKeyDown={handleKeyDown} className="input-field text-sm flex-1" />
              <button onClick={addRule} className="btn-primary text-sm whitespace-nowrap flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                เพิ่มกฎ
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-2">💡 กด Enter เพื่อเพิ่มกฎได้เร็วขึ้น</p>
          </div>

          {/* Quick Preset Rules */}
          <div className="mt-4 space-y-3">
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">📋 กฎสำเร็จรูป</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { chain: 'forward', action: 'drop', src_address: '192.168.20.0/24', dst_address: '192.168.10.0/24', comment: 'บล็อก Guest ไป Staff', protocol: '' },
                  { chain: 'input', action: 'drop', dst_port: '22', comment: 'บล็อก SSH จากภายนอก', protocol: 'tcp' },
                  { chain: 'input', action: 'drop', dst_port: '3389', comment: 'บล็อก Remote Desktop', protocol: 'tcp' },
                  { chain: 'forward', action: 'drop', dst_port: '445', comment: 'บล็อก SMB/WannaCry', protocol: 'tcp' },
                  { chain: 'input', action: 'accept', dst_port: '8291', comment: 'อนุญาต WinBox', protocol: 'tcp' },
                ].map((preset, i) => (
                  <button key={i} onClick={() => { const updated = { ...form, rules: [...(form.rules || []), preset] }; setForm(updated); onChange(updated); }} className="px-2.5 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-xs text-gray-600 dark:text-gray-400 hover:border-blue-400 hover:text-blue-600 dark:hover:border-blue-500 dark:hover:text-blue-400 transition-colors">
                    + {preset.comment}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/50 rounded-xl">
              <h4 className="text-xs font-bold text-red-800 dark:text-red-300 mb-2">🚫 บล็อก BitTorrent</h4>
              <div className="flex flex-wrap gap-2">
                {[
                  { chain: 'forward', action: 'drop', dst_port: '6881-6999', comment: 'บล็อก BitTorrent TCP', protocol: 'tcp' },
                  { chain: 'forward', action: 'drop', dst_port: '6881-6999', comment: 'บล็อก BitTorrent UDP', protocol: 'udp' },
                ].map((preset, i) => (
                  <button key={i} onClick={() => { const updated = { ...form, rules: [...(form.rules || []), preset] }; setForm(updated); onChange(updated); }} className="px-2.5 py-1.5 bg-white dark:bg-gray-800 border border-red-200 dark:border-red-700 rounded-lg text-xs text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors">
                    + {preset.comment}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-3 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800/50 rounded-xl">
              <h4 className="text-xs font-bold text-orange-800 dark:text-orange-300 mb-2">🎮 บล็อกเกม</h4>
              <div className="flex flex-wrap gap-2">
                {[
                  { chain: 'forward', action: 'drop', dst_port: '5000-5010', comment: 'บล็อก ROV', protocol: 'tcp' },
                  { chain: 'forward', action: 'drop', dst_port: '27015-27050', comment: 'บล็อก Steam', protocol: 'tcp' },
                  { chain: 'forward', action: 'drop', dst_port: '3478-3480', comment: 'บล็อก Valorant', protocol: 'tcp' },
                ].map((preset, i) => (
                  <button key={i} onClick={() => { const updated = { ...form, rules: [...(form.rules || []), preset] }; setForm(updated); onChange(updated); }} className="px-2.5 py-1.5 bg-white dark:bg-gray-800 border border-orange-200 dark:border-orange-700 rounded-lg text-xs text-orange-700 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors">
                    + {preset.comment}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NAT Rules Tab */}
      {activeTab === 'nat' && (
        <div>
          {form.nat_rules && form.nat_rules.length > 0 && (
            <div className="space-y-2 mb-4">
              {form.nat_rules.map((rule, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-100 dark:border-cyan-800/50 animate-fadeIn">
                  <div className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                    <span className="inline-flex items-center gap-1">
                      <span className="font-mono text-xs bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 px-1.5 py-0.5 rounded">{rule.chain}</span>
                      <span className="text-gray-400">→</span>
                      <span className="font-mono text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded">{rule.action}</span>
                    </span>
                    {rule.protocol && <span className="ml-1 text-xs text-gray-400">({rule.protocol})</span>}
                    {rule.in_interface && <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">in:{rule.in_interface}</span>}
                    {rule.out_interface && <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">out:{rule.out_interface}</span>}
                    {rule.src_address && <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">จาก {rule.src_address}</span>}
                    {rule.dst_address && <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">ไป {rule.dst_address}</span>}
                    {rule.dst_port && <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">พอร์ต {rule.dst_port}</span>}
                    {rule.to_addresses && <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">→ {rule.to_addresses}</span>}
                    {rule.to_ports && <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">:{rule.to_ports}</span>}
                    <div className="text-xs text-gray-400 mt-1">💬 {rule.comment}</div>
                  </div>
                  <button onClick={() => removeNatRule(i)} className="text-red-400 hover:text-red-600 ml-2 text-xl leading-none flex-shrink-0" title="ลบกฎนี้">×</button>
                </div>
              ))}
            </div>
          )}

          {/* Add NAT Rule Form */}
          <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl border border-gray-200 dark:border-gray-600">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-3">➕ เพิ่มกฎ NAT ใหม่</p>

            {natErrorMsg && (
              <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-xs text-red-600 dark:text-red-400 animate-fadeIn">
                ⚠️ {natErrorMsg}
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
              <div>
                <label className="label text-xs">NAT Chain</label>
                <select value={newNatRule.chain} onChange={e => setNewNatRule({ ...newNatRule, chain: e.target.value })} className="input-field text-sm">
                  <option value="srcnat">srcnat ({natChainLabels.srcnat})</option>
                  <option value="dstnat">dstnat ({natChainLabels.dstnat})</option>
                </select>
              </div>
              <div>
                <label className="label text-xs">NAT Action</label>
                <select value={newNatRule.action} onChange={e => setNewNatRule({ ...newNatRule, action: e.target.value })} className="input-field text-sm">
                  {newNatRule.chain === 'srcnat' ? (
                    <>
                      <option value="masquerade">Masquerade (ใช้ IP ของ WAN)</option>
                      <option value="src-nat">Source NAT (เปลี่ยน IP ต้นทาง)</option>
                      <option value="netmap">Netmap (แมป IP 1:1)</option>
                    </>
                  ) : (
                    <>
                      <option value="dst-nat">Destination NAT (เปลี่ยน IP ปลายทาง)</option>
                      <option value="redirect">Redirect (เปลี่ยนพอร์ต)</option>
                      <option value="netmap">Netmap (แมป IP 1:1)</option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="label text-xs">โปรโตคอล</label>
                <select value={newNatRule.protocol} onChange={e => setNewNatRule({ ...newNatRule, protocol: e.target.value })} className="input-field text-sm">
                  <option value="tcp">TCP</option>
                  <option value="udp">UDP</option>
                  <option value="tcp,udp">TCP + UDP</option>
                  <option value="icmp">ICMP</option>
                  <option value="">ทั้งหมด</option>
                </select>
              </div>
              <div>
                <label className="label text-xs">In. Interface (ขาเข้า)</label>
                <select value={newNatRule.in_interface} onChange={e => setNewNatRule({ ...newNatRule, in_interface: e.target.value })} className="input-field text-sm">
                  <option value="">-- ทั้งหมด --</option>
                  {(interfaceNames && interfaceNames.length > 0 ? interfaceNames : ['ether1', 'ether2', 'ether3', 'ether4', 'ether5', 'bridge1', 'sfp-sfpplus1']).map(iface => (
                    <option key={iface} value={iface}>{iface}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label text-xs">Out. Interface (ขาออก)</label>
                <select value={newNatRule.out_interface} onChange={e => setNewNatRule({ ...newNatRule, out_interface: e.target.value })} className="input-field text-sm">
                  <option value="">-- ทั้งหมด --</option>
                  {(interfaceNames && interfaceNames.length > 0 ? interfaceNames : ['ether1', 'ether2', 'ether3', 'ether4', 'ether5', 'bridge1', 'sfp-sfpplus1']).map(iface => (
                    <option key={iface} value={iface}>{iface}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label text-xs">พอร์ตปลายทาง (Dst Port)</label>
                <input type="text" placeholder="เช่น 80, 443, 8080" value={newNatRule.dst_port} onChange={e => setNewNatRule({ ...newNatRule, dst_port: e.target.value })} className="input-field text-sm" />
              </div>
              <div>
                <label className="label text-xs">IP ต้นทาง (Src Address)</label>
                <input type="text" placeholder="เช่น 192.168.88.0/24" value={newNatRule.src_address} onChange={e => setNewNatRule({ ...newNatRule, src_address: e.target.value })} className="input-field text-sm" />
              </div>
              <div>
                <label className="label text-xs">IP ปลายทาง (Dst Address)</label>
                <input type="text" placeholder="เช่น 203.0.113.1" value={newNatRule.dst_address} onChange={e => setNewNatRule({ ...newNatRule, dst_address: e.target.value })} className="input-field text-sm" />
              </div>
              <div>
                <label className="label text-xs">To Addresses (IP ใหม่)</label>
                <input type="text" placeholder="เช่น 192.168.88.100" value={newNatRule.to_addresses} onChange={e => setNewNatRule({ ...newNatRule, to_addresses: e.target.value })} className="input-field text-sm" />
              </div>
              <div>
                <label className="label text-xs">To Ports (พอร์ตใหม่)</label>
                <input type="text" placeholder="เช่น 80 (เว้นว่าง = เดิม)" value={newNatRule.to_ports} onChange={e => setNewNatRule({ ...newNatRule, to_ports: e.target.value })} className="input-field text-sm" />
              </div>
            </div>
            <div className="flex gap-2">
              <input type="text" placeholder="💬 คำอธิบายกฎ (จำเป็นต้องกรอก)" value={newNatRule.comment} onChange={e => { setNewNatRule({ ...newNatRule, comment: e.target.value }); setNatErrorMsg(''); }} onKeyDown={handleNatKeyDown} className="input-field text-sm flex-1" />
              <button onClick={addNatRule} className="btn-primary text-sm whitespace-nowrap flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                เพิ่มกฎ NAT
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-2">💡 กด Enter เพื่อเพิ่มกฎได้เร็วขึ้น</p>
          </div>

          {/* NAT Presets */}
          <div className="mt-4 space-y-3">
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">📋 กฎ NAT สำเร็จรูป</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { chain: 'srcnat', protocol: '', dst_port: '', in_interface: '', out_interface: '', src_address: '192.168.88.0/24', dst_address: '', action: 'masquerade', to_addresses: '', to_ports: '', comment: 'Masquerade LAN' },
                  { chain: 'dstnat', protocol: 'tcp', dst_port: '80', in_interface: 'ether1', out_interface: '', src_address: '', dst_address: '', action: 'dst-nat', to_addresses: '192.168.88.10', to_ports: '80', comment: 'Port Forward Web Server' },
                  { chain: 'dstnat', protocol: 'tcp', dst_port: '443', in_interface: 'ether1', out_interface: '', src_address: '', dst_address: '', action: 'dst-nat', to_addresses: '192.168.88.10', to_ports: '443', comment: 'Port Forward HTTPS' },
                  { chain: 'dstnat', protocol: 'tcp', dst_port: '3389', in_interface: 'ether1', out_interface: '', src_address: '', dst_address: '', action: 'dst-nat', to_addresses: '192.168.88.20', to_ports: '3389', comment: 'Port Forward RDP' },
                  { chain: 'srcnat', protocol: '', dst_port: '', in_interface: '', out_interface: 'pppoe-out1', src_address: '192.168.88.0/24', dst_address: '', action: 'masquerade', to_addresses: '', to_ports: '', comment: 'Masquerade ผ่าน PPPoE' },
                ].map((preset, i) => (
                  <button key={i} onClick={() => { const updated = { ...form, nat_rules: [...(form.nat_rules || []), preset] }; setForm(updated); onChange(updated); }} className="px-2.5 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-xs text-gray-600 dark:text-gray-400 hover:border-cyan-400 hover:text-cyan-600 dark:hover:border-cyan-500 dark:hover:text-cyan-400 transition-colors">
                    + {preset.comment}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between mt-6">
        <button onClick={onBack} className="btn-secondary">← ย้อนกลับ</button>
        <button onClick={onNext} className="btn-primary">ถัดไป ({(form.rules || []).length} Filter + {(form.nat_rules || []).length} NAT)</button>
      </div>
    </div>
  );
}
